import React from 'react'

import { css, Global, useTheme } from '@emotion/react'

import { useLayerManager } from '@surglogs-app/ui'

const DATEPICKER_PORTAL_ID = 'datepicker-portal'
const POPPER_Z_INDEX_OFFSET = 9

export { DATEPICKER_PORTAL_ID }

export const DatePickerPortalStyles = () => {
  const theme = useTheme()
  const { zIndex, increaseBy } = useLayerManager()
  const popperZIndex = zIndex + increaseBy + POPPER_Z_INDEX_OFFSET

  return (
    <Global
      {...{
        styles: css`
          #${DATEPICKER_PORTAL_ID} {
            .react-datepicker {
              border: 1px solid ${theme.ui.colors['navy.25']};
              font-family: ${theme.ui.typography.fontFamilies.default};
              padding: ${theme.ui.spacings.md};
              padding-bottom: ${theme.ui.spacings.xs};
            }

            .react-datepicker__header {
              padding: 0;
              background-color: ${theme.ui.colors['white.100']};
              border-bottom: none;
              display: flex;
              flex-wrap: wrap;
              width: min-content;
            }

            .react-datepicker__current-month {
              display: none;
            }

            .react-datepicker__header__dropdown {
              margin-top: 1px;
              margin-bottom: ${theme.ui.spacings.sm};

              select {
                font-family: ${theme.ui.typography.fontFamilies.default};
                font-weight: ${theme.ui.typography.fontWeights.semiBold};
                font-size: ${theme.ui.typography.fontSizes.sm};
                border: none;
                background: none;
              }

              select:focus-visible {
                outline: none;
              }
            }

            .react-datepicker__month-dropdown-container {
              margin-right: ${theme.ui.spacings.sm};
              margin-left: 0;
            }

            .react-datepicker__month {
              margin: 0;
            }

            .react-datepicker__navigation {
              top: 15px;
              height: auto;
              width: 10px;
            }

            .react-datepicker__navigation--previous {
              right: 45px;
              left: unset;
            }

            .react-datepicker__navigation--next {
              right: 20px;
            }

            .react-datepicker__navigation-icon::before {
              border-color: ${theme.ui.colors['navy.100']};
              border-width: 2px 2px 0 0;
            }

            .react-datepicker__navigation:hover *::before {
              border-color: ${theme.ui.colors['blue.100']};
            }

            .react-datepicker__day-names {
              display: flex;
              margin-bottom: 0;
            }

            .react-datepicker__week {
              display: flex;
            }

            .react-datepicker__day-name,
            .react-datepicker__day {
              box-sizing: border-box;
              font-size: ${theme.ui.typography.fontSizes.xxs};
              text-transform: uppercase;
              border-left: 2px solid ${theme.ui.colors['white.100']};
              border-right: 2px solid ${theme.ui.colors['white.100']};
              height: 36px;
              width: 40px;
              padding: 0;
              margin: 0;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .react-datepicker__day-name {
              font-weight: ${theme.ui.typography.fontWeights.semiBold};
              color: ${theme.ui.colors['navy.100']};
            }

            .react-datepicker__day {
              font-weight: ${theme.ui.typography.fontWeights.regular};
              color: ${theme.ui.colors['navy.100']};
            }

            .react-datepicker__month-select,
            .react-datepicker__year-select {
              color: ${theme.ui.colors['navy.100']};
            }

            .react-datepicker__day:focus-visible {
              outline: none;
            }

            .react-datepicker__year-dropdown-container--select {
              margin: 0;
            }

            .react-datepicker__day--selected,
            .react-datepicker__day--in-selecting-range,
            .react-datepicker__day--in-range,
            .react-datepicker__month-text--selected,
            .react-datepicker__month-text--in-selecting-range,
            .react-datepicker__month-text--in-range,
            .react-datepicker__quarter-text--selected,
            .react-datepicker__quarter-text--in-selecting-range,
            .react-datepicker__quarter-text--in-range,
            .react-datepicker__year-text--selected,
            .react-datepicker__year-text--in-selecting-range,
            .react-datepicker__year-text--in-range,
            .react-datepicker__day--keyboard-selected,
            .react-datepicker__month-text--keyboard-selected,
            .react-datepicker__quarter-text--keyboard-selected,
            .react-datepicker__year-text--keyboard-selected,
            .react-datepicker__day--selected:hover,
            .react-datepicker__day--in-selecting-range:hover,
            .react-datepicker__day--in-range:hover,
            .react-datepicker__month-text--selected:hover,
            .react-datepicker__month-text--in-selecting-range:hover,
            .react-datepicker__month-text--in-range:hover,
            .react-datepicker__quarter-text--selected:hover,
            .react-datepicker__quarter-text--in-selecting-range:hover,
            .react-datepicker__quarter-text--in-range:hover,
            .react-datepicker__year-text--selected:hover,
            .react-datepicker__year-text--in-selecting-range:hover,
            .react-datepicker__year-text--in-range:hover,
            .react-datepicker__day--keyboard-selected:hover,
            .react-datepicker__month-text--keyboard-selected:hover,
            .react-datepicker__quarter-text--keyboard-selected:hover,
            .react-datepicker__year-text--keyboard-selected:hover {
              background-color: ${theme.ui.colors['blue.100']};
            }

            .react-datepicker__day--selected.react-datepicker__day--today {
              background-color: ${theme.ui.colors['blue.100']};
              color: ${theme.ui.colors['white.100']};
              border-radius: 0.3rem;
              border-bottom: none;
            }

            .react-datepicker__day--selected.react-datepicker__day--today:hover {
              background-color: ${theme.ui.colors['blue.100']};
              color: ${theme.ui.colors['white.100']};
              border-bottom: none;
            }

            .react-datepicker__day--today {
              color: ${theme.ui.colors['blue.100']};
              background-color: ${theme.ui.colors['white.100']};
              border-left: none;
              border-right: none;
              border-bottom: 2px solid ${theme.ui.colors['blue.100']};
              border-radius: 0;
              font-weight: ${theme.ui.typography.fontWeights.semiBold};
            }

            .react-datepicker__day:hover {
              background-color: ${theme.ui.colors.background};
            }

            .react-datepicker__day--today:hover {
              background-color: ${theme.ui.colors.background};
              border-radius: 0.3rem;
              border-color: transparent;
              color: ${theme.ui.colors['navy.100']};
              font-weight: ${theme.ui.typography.fontWeights.regular};
            }

            .react-datepicker__day--selected {
              color: ${theme.ui.colors['white.100']};
            }

            .react-datepicker__day--selected:hover {
              color: ${theme.ui.colors['white.100']};
              background-color: ${theme.ui.colors['blue.100']};
            }

            .react-datepicker-popper {
              z-index: ${popperZIndex};
            }
          }
        `,
      }}
    />
  )
}
